import { Flex } from "@chakra-ui/react";
import { RootState, useAppDispatch, useAppSelector } from "../../store";
import { useEffect, useState } from "react";
import { transformGuides } from "../../helpers/transformGuides";
import { FormikProps } from "formik";
import { ApplicationForm } from "../../model/applications";
import { clearCurrentProject, fetchSingleProject } from "../../store/slices/projects";
import { fetchAllProjects } from "../../store/slices/projects";
import FileUploader from "../FileUploader/FileUploader";
import { BaseInput } from "../Controls/BaseInput";
import { BaseTextArea } from "../Controls/BaseTextArea";
import { SalesMaxOfferBlock } from "../Controls/SalesMaxOfferBlock";
import { FormWrapper } from "../FormWrapper/FormWrapper";
import { Customer } from "@/model/customers";
import { BaseSelect, SelectOption } from "../Controls/BaseSelect";
import { CreatableProfileSelect } from "../Controls/CreatableProfileSelect";

interface UpdateApplicationBlockProps {
  formik: FormikProps<ApplicationForm>;
}

export const UpdateApplicationBlock = ({ formik }: UpdateApplicationBlockProps) => {
  const { currentApplication } = useAppSelector((state: RootState) => state.applications);

  const { guides } = useAppSelector((state: RootState) => state.guides);

  const dispatch = useAppDispatch();

  const { projects, currentProject } = useAppSelector((state: RootState) => state.projects);

  const { customers } = useAppSelector((state: RootState) => state.customers);

  const projectsOptions = projects.items.map((el) => ({
    value: el.id,
    label: el.name,
  }));
  useEffect(() => {
    if (formik.values.customer_id !== null && formik.values.customer_id !== undefined) {
      dispatch(fetchAllProjects({ params: { filters: { customer_id: formik.values.customer_id.toString() } } }));
    }
  }, [dispatch, formik.values.customer_id]);

  useEffect(() => {
    if (currentProject && formik.values.project_id === currentProject.id) {
      formik.setValues({
        ...formik.values,
        customer_id: currentProject.customer?.id ?? null,
        project_description: currentProject.description ?? "",
        legal_entity_id: currentProject.legal_entity?.id ?? null,
        bitrix_contract_link: (currentProject as any).bitrix_contract_link ?? "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject, formik.values.project_id]);

  const [jobFormatOptions, setJobFormatOptions] = useState<SelectOption[]>([]);
  const [jobWorkloadOptions, setJobWorkloadOptions] = useState<SelectOption[]>([]);
  const [attractionPeriodOptions, setAttractionPeriodOptions] = useState<SelectOption[]>([]);
  const [profileOptions, setProfileOptions] = useState<SelectOption[]>([]);
  const [legalEntityOptions, setLegalEntityOptions] = useState<SelectOption[]>([]);
  const [gradeOptions, setGradeOptions] = useState<SelectOption[]>([]);
  const [statusOptions, setStatusOptions] = useState<SelectOption[]>([]);
  const [currentFiles, setCurrentFiles] = useState<any>([]);

  const customerOptions: SelectOption[] = customers.items.map((el: Customer) => {
    const value = el.id !== undefined && typeof el.id === "number" ? el.id : undefined;
    const label = el.name !== undefined && typeof el.name === "string" ? el.name : undefined;

    return {
      value: value,
      label: label || "",
    };
  });

  useEffect(() => {
    if (guides) {
      const resultJobFormatList = transformGuides(guides.JobFormat).map((o: any) => ({ value: String(o.value), label: String(o.label || "") }));
      setJobFormatOptions(resultJobFormatList);
      const resultJobWorkloadList = transformGuides(guides.JobWorkload).map((o: any) => ({ value: String(o.value), label: String(o.label || "") }));
      setJobWorkloadOptions(resultJobWorkloadList);
      const resultAttractionPeriodList = transformGuides(guides.AttractionPeriod).map((o: any) => ({
        value: String(o.value),
        label: String(o.label || ""),
      }));
      setAttractionPeriodOptions(resultAttractionPeriodList);
      const resultProfileList = transformGuides(guides.ApplicationProfile).map((o: any) => ({
        value: Number(o.value),
        label: String(o.label || ""),
      }));
      setProfileOptions(resultProfileList);
      const resultLegalEntityList = transformGuides(guides.LegalEntity).map((o: any) => ({ value: Number(o.value), label: String(o.label || "") }));
      setLegalEntityOptions(resultLegalEntityList);
      const grades = (transformGuides(guides.Grade) as any[]).map((o) => ({
        value: String(o.value),
        label: String(o.label || ""),
      })) as unknown as SelectOption[];
      setGradeOptions(grades);
      const resultStatusList = transformGuides((guides as any).ApplicationStatus).map((o: any) => ({
        value: Number(o.value),
        label: String(o.label || ""),
      }));
      setStatusOptions(resultStatusList);
    }
  }, [guides]);

  useEffect(() => {
    const resultFiles =
      currentApplication?.attachments &&
      Object.entries(currentApplication.attachments).map(([key, el]) => {
        return {
          name: (el as any).name,
          id: key,
          isCurrentFile: true,
        };
      });
    setCurrentFiles(resultFiles);
  }, [currentApplication]);

  return (
    <FormWrapper>
      <Flex gap="48px" paddingBottom="20px">
        <BaseInput
          fieldTitle={"Название должности"}
          requiredField={true}
          value={formik.values.name}
          onChange={formik.handleChange}
          name="name"
          placeholder="Введите название должности"
          containerStyles={{ width: "100%" }}
        />
        <BaseSelect
          fieldTitle="Заказчик"
          requiredField
          options={customerOptions}
          placeholder="Выберите заказчика"
          value={formik.values.customer_id}
          onChange={(value) => {
            const nextCustomerId = value ? Number(value) : null;
            dispatch(clearCurrentProject());
            formik.setValues({
              ...formik.values,
              customer_id: nextCustomerId,
              project_id: null,
              project_description: "",
              legal_entity_id: null,
              bitrix_contract_link: "",
            });
          }}
          containerStyles={{ width: "100%" }}
          isEmpty={formik.values.customer_id === null}
        />
      </Flex>

      <Flex gap="48px" paddingBottom="20px">
        <BaseSelect
          fieldTitle="Проект"
          options={projectsOptions}
          placeholder="Выберите проект"
          key={`project-select-${formik.values.customer_id ?? "none"}`}
          value={formik.values.project_id}
          onChange={(value) => {
            const id = value ? Number(value) : null;
            if (id === null) {
              formik.setValues({
                ...formik.values,
                project_id: null,
                project_description: "",
                legal_entity_id: null,
                bitrix_contract_link: "",
              });
            } else {
              formik.setFieldValue("project_id", id);
              dispatch(fetchSingleProject({ id }));
            }
          }}
          containerStyles={{ width: "100%" }}
        />
        <BaseSelect
          fieldTitle="Статус заявки"
          requiredField
          options={statusOptions}
          placeholder="Выберите статус"
          value={formik.values.status as any}
          onChange={(value) => formik.setFieldValue("status", value ? Number(value) : undefined)}
          containerStyles={{ width: "100%" }}
          isEmpty={!formik.values.status && formik.values.status !== 0}
        />
      </Flex>

      <Flex gap="48px" paddingBottom="20px">
        <BaseSelect
          fieldTitle="Юр. лицо"
          options={legalEntityOptions}
          requiredField={true}
          placeholder="Выберите юр. лицо"
          value={formik.values.legal_entity_id}
          onChange={(value) => formik.setFieldValue("legal_entity_id", value ? Number(value) : null)}
          containerStyles={{ width: "100%", minWidth: "300px" }}
        />
        <CreatableProfileSelect
          fieldTitle="Профиль"
          requiredField
          options={profileOptions}
          placeholder="Выберите профиль или начните вводить для поиска"
          value={formik.values.profile_id}
          onChange={(value) => formik.setFieldValue("profile_id", value ? Number(value) : null)}
          containerStyles={{ width: "100%", minWidth: "300px" }}
          isEmpty={!formik.values.profile_id}
        />
        <BaseSelect
          fieldTitle="Грейд"
          requiredField
          options={gradeOptions}
          placeholder="Выберите грейд"
          value={formik.values.candidate_grade}
          onChange={(value) => formik.setFieldValue("candidate_grade", Array.isArray(value) ? value : [])}
          containerStyles={{ width: "100%", minWidth: "300px" }}
          isMulti
          isEmpty={!formik.values.candidate_grade || formik.values.candidate_grade.length === 0}
        />
      </Flex>

      <Flex gap="20px" paddingBottom="20px" flexDirection="column">
        <BaseTextArea
          fieldTitle={"Требования к кандидату"}
          requiredField={true}
          id="candidate_requirements"
          value={formik.values.candidate_requirements}
          onChange={formik.handleChange}
          placeholder="Опишите требования к кандидату"
        />

        <BaseTextArea
          fieldTitle={"Обязанности кандидата"}
          id="job_duties"
          requiredField={true}
          value={formik.values.job_duties}
          onChange={formik.handleChange}
          placeholder="Опишите обязанности кандидата"
        />

        <BaseTextArea
          fieldTitle="Описание проекта"
          name={"project_description"}
          onChange={formik.handleChange}
          value={formik.values.project_description}
          placeholder="Краткое описание проекта"
          requiredField
        />

        <BaseInput
          fieldTitle={"Ссылка на договор (Bitrix 24)"}
          value={(formik.values as any).bitrix_contract_link || ""}
          onChange={formik.handleChange}
          name="bitrix_contract_link"
          placeholder="Вставьте ссылку на договор"
        />

        <SalesMaxOfferBlock
          valueGeneral={(formik.values as any).sales_max_offer_general || ""}
          value1={formik.values.sales_max_offer_moscow || ""}
          value2={formik.values.sales_max_offer_regions || ""}
          idGeneral="sales_max_offer_general"
          id1="sales_max_offer_moscow"
          id2="sales_max_offer_regions"
          onChangeGeneral={(value) => formik.setFieldValue("sales_max_offer_general", value)}
          onChange1={(value) => formik.setFieldValue("sales_max_offer_moscow", value)}
          onChange2={(value) => formik.setFieldValue("sales_max_offer_regions", value)}
        />
      </Flex>

      <Flex gap="48px" paddingTop="20px" paddingBottom="20px">
        <BaseInput
          fieldTitle="Маржинальность"
          onChange={formik.handleChange}
          value={`${formik.values.marginality ?? ""}`}
          name={"marginality"}
          placeholder="Введите маржинальность"
          containerStyles={{ flex: 1 }}
          requiredField={true}
        />
        <BaseInput
          fieldTitle="Ставка закупки (max)"
          onChange={formik.handleChange}
          value={(formik.values as any).purchase_rate_max || ""}
          name={"purchase_rate_max"}
          placeholder="Введите максимальную ставку закупки"
          containerStyles={{ flex: 1 }}
        />
        <BaseInput
          fieldTitle="Сколько человек нужно нанять"
          value={`${formik.values.required_number ?? ""}`}
          onChange={formik.handleChange}
          name="required_number"
          requiredField={true}
          placeholder="Введите количество человек"
          containerStyles={{ flex: 1 }}
        />
      </Flex>

      <Flex gap="48px" paddingBottom="20px">
        <BaseInput
          fieldTitle="Локация поиска"
          value={formik.values.location}
          onChange={formik.handleChange}
          name="location"
          placeholder="Город/регион"
          containerStyles={{ flex: 1 }}
        />
        <BaseSelect
          fieldTitle="Формат работы"
          options={jobFormatOptions}
          placeholder="Выберите формат"
          value={formik.values.job_format}
          onChange={(value) => formik.setFieldValue("job_format", value)}
          containerStyles={{ flex: 1 }}
          requiredField
        />
      </Flex>

      <BaseTextArea
        fieldTitle="Адрес офиса"
        name={"office_address"}
        onChange={formik.handleChange}
        value={formik.values.office_address}
        placeholder="Укажите адрес офиса (если есть)"
      />

      <Flex gap="48px" paddingTop="20px" paddingBottom="20px">
        <BaseSelect
          fieldTitle="Занятость"
          options={jobWorkloadOptions}
          placeholder="Выберите занятость"
          value={formik.values.job_workload}
          onChange={(value) => formik.setFieldValue("job_workload", value)}
          requiredField={true}
          containerStyles={{ width: "100%" }}
        />
        <BaseSelect
          fieldTitle="Срок привлечения"
          options={attractionPeriodOptions}
          placeholder="Выберите срок"
          value={formik.values.attraction_period}
          onChange={(value) => formik.setFieldValue("attraction_period", value)}
          containerStyles={{ width: "100%" }}
        />
      </Flex>

      <BaseTextArea
        fieldTitle={"Комментарии к заявке"}
        id="comments"
        value={formik.values.comments}
        onChange={formik.handleChange}
        placeholder="Добавьте комментарий"
      />

      <Flex className={"form-column"} flex="1" paddingTop="20px">
        <FileUploader
          onChangeDeletedIds={(ids) => formik.setFieldValue("attachments_delete", ids)}
          onChange={(files) => formik.setFieldValue("attachments", files)}
          currentFiles={currentFiles}
        />
      </Flex>
    </FormWrapper>
  );
};
